import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Stack, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
  InputAdornment, CircularProgress, Avatar, MenuItem, Autocomplete,
  Chip, Tooltip
} from "@mui/material";
import { 
  Add, Edit, Delete, Search, CloudUpload, Receipt,
  CheckCircle, Cancel, HourglassEmpty, AdminPanelSettings 
} from "@mui/icons-material";
import {
  collection, query, where, getDocs, addDoc, updateDoc,
  deleteDoc, doc, Timestamp, orderBy, getDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../../firebase";
import Swal from "sweetalert2";

function Expense() {
  const [expenses, setExpenses] = useState([]);
  const [ledgers, setLedgers] = useState([]); 
  const [banks, setBanks] = useState([]); 
  const [uniquePersons, setUniquePersons] = useState([]); 
  const [uniqueExpenseHeads, setUniqueExpenseHeads] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schoolId, setSchoolId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog State
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    expenseHead: "",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    description: "",
    personName: "",
    ledger: "", 
    imageUrl: "",
    payMode: "Cash",
    dayBook: "Yes",
    bankId: "",
    bankName: "",
    bankBranch: "",
    bankCode: "",
    transactionId: ""
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Admin Approval Dialog States
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedItemForApproval, setSelectedItemForApproval] = useState(null);
  const [adminNote, setAdminNote] = useState("");

  useEffect(() => {
    getInitialData();
  }, []);

  const getInitialData = async () => {
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const userData = userSnap.data();
      const sId = userData?.schoolId;
      setSchoolId(sId);
      setIsAdmin(userData?.role === "admin" || userData?.isAdmin === true);

      if (sId) {
        await fetchLedgers(sId);
        await fetchBanks(sId);
        await fetchExpenses(sId);
      }
    } catch (e) {
      console.error("Initialization Error:", e);
    }
  };

  const fetchLedgers = async (sId) => {
    try {
      const q = query(collection(db, "ledgers"));
      const snap = await getDocs(q);
      setLedgers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Error fetching ledgers:", e);
    }
  };

  const fetchBanks = async (sId) => {
    try {
      const q = query(collection(db, "banks"), where("schoolId", "==", sId));
      const snap = await getDocs(q);
      setBanks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Error fetching banks:", e);
    }
  };

  const fetchExpenses = async (sId) => {
    setLoading(true);
    try {
      const q = query(collection(db, "expenses"), where("schoolId", "==", sId), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      const fetchedExpenses = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setExpenses(fetchedExpenses);
      
      const names = fetchedExpenses
        .map(exp => exp.personName)
        .filter((name, index, self) => name && self.indexOf(name) === index);
      setUniquePersons(names);

      const heads = fetchedExpenses
        .map(exp => exp.expenseHead)
        .filter((head, index, self) => head && self.indexOf(head) === index);
      setUniqueExpenseHeads(heads);

    } catch (e) {
      console.error("Error fetching expenses:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!file) return formData.imageUrl || "";
    const storageRef = ref(storage, `expenses/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleSubmit = async () => {
    if (!formData.amount || !formData.personName || !formData.ledger || !formData.expenseHead) {
      return Swal.fire("Error", "Expense Head, Recipient/Person, Amount, and Ledger mapping are required", "error");
    }

    setSubmitting(true);
    try {
      const imageUrl = await handleFileUpload();

      if (editId) {
        // Edit requires Admin Approval
        const pendingPayload = {
          ...formData,
          amount: parseFloat(formData.amount).toString(),
          imageUrl,
          requestedAt: Timestamp.now(),
          requestedBy: auth.currentUser?.email || "User"
        };

        await updateDoc(doc(db, "expenses", editId), {
          approvalStatus: "pending_edit",
          pendingData: pendingPayload,
          updatedAt: Timestamp.now()
        });

        Swal.fire("Request Submitted", "Edit request submitted for admin approval.", "info");
      } else {
        // New entries are directly created with approved status
        const payload = {
          ...formData,
          amount: parseFloat(formData.amount).toString(),
          imageUrl,
          schoolId,
          dayBook: formData.dayBook || "Yes",
          approvalStatus: "approved",
          approvalNote: "",
          pendingData: null,
          updatedAt: Timestamp.now(),
          createdAt: Timestamp.now()
        };

        await addDoc(collection(db, "expenses"), payload);
        Swal.fire("Success", "Expense transaction saved successfully", "success");
      }

      setOpen(false);
      resetForm();
      fetchExpenses(schoolId);
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const requestDelete = async (item) => {
    const res = await Swal.fire({
      title: 'Request Delete Approval?',
      text: "This action will submit a deletion request to the admin.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Submit Request'
    });

    if (res.isConfirmed) {
      try {
        await updateDoc(doc(db, "expenses", item.id), {
          approvalStatus: "pending_delete",
          updatedAt: Timestamp.now()
        });
        fetchExpenses(schoolId);
        Swal.fire("Request Sent", "Deletion request submitted for admin approval.", "info");
      } catch (e) {
        Swal.fire("Error", e.message, "error");
      }
    }
  };

  // Admin Approval Decision Logic
  const handleApprovalAction = async (item, action) => {
    try {
      if (action === "approve") {
        if (item.approvalStatus === "pending_delete") {
          await deleteDoc(doc(db, "expenses", item.id));
          Swal.fire("Approved & Deleted", "Record has been permanently deleted.", "success");
        } else if (item.approvalStatus === "pending_edit") {
          const updatedFields = item.pendingData;
          await updateDoc(doc(db, "expenses", item.id), {
            ...updatedFields,
            approvalStatus: "approved",
            pendingData: null,
            approvalNote: adminNote || "Edit approved by admin",
            updatedAt: Timestamp.now()
          });
          Swal.fire("Approved", "Edit changes applied successfully.", "success");
        }
      } else if (action === "reject") {
        await updateDoc(doc(db, "expenses", item.id), {
          approvalStatus: "approved",
          pendingData: null,
          approvalNote: adminNote || "Request rejected by admin",
          updatedAt: Timestamp.now()
        });
        Swal.fire("Rejected", "Request has been rejected.", "info");
      }

      setApprovalModalOpen(false);
      setSelectedItemForApproval(null);
      setAdminNote("");
      fetchExpenses(schoolId);
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  const resetForm = () => {
    setFormData({
      expenseHead: "",
      amount: "",
      date: new Date().toISOString().split('T')[0],
      description: "",
      personName: "",
      ledger: "",
      imageUrl: "",
      payMode: "Cash",
      dayBook: "Yes",
      bankId: "",
      bankName: "",
      bankBranch: "",
      bankCode: "",
      transactionId: ""
    });
    setEditId(null);
    setFile(null);
  };

  const filteredExpenses = expenses.filter(exp =>
    exp.personName?.toLowerCase().includes(search.toLowerCase()) ||
    exp.expenseHead?.toLowerCase().includes(search.toLowerCase()) ||
    exp.description?.toLowerCase().includes(search.toLowerCase()) ||
    exp.payMode?.toLowerCase().includes(search.toLowerCase()) ||
    exp.dayBook?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusChip = (status) => {
    switch (status) {
      case "pending_edit":
        return <Chip label="Pending Edit" color="warning" size="small" icon={<HourglassEmpty sx={{ fontSize: '14px' }} />} sx={{ fontSize: '11px', height: 22 }} />;
      case "pending_delete":
        return <Chip label="Pending Delete" color="error" size="small" icon={<HourglassEmpty sx={{ fontSize: '14px' }} />} sx={{ fontSize: '11px', height: 22 }} />;
      default:
        return <Chip label="Approved" color="success" size="small" variant="outlined" sx={{ fontSize: '11px', height: 22 }} />;
    }
  };

  return (
    <Box p={1} bgcolor="#f8fafc" minHeight="100vh">
      {/* Header Section */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="error.dark" sx={{ lineHeight: 1.2 }}>Expense Manager</Typography>
          <Typography variant="caption" color="text.secondary">Track payments and debit allocations</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          size="small"
          onClick={() => { resetForm(); setOpen(true); }}
          sx={{ borderRadius: 1.5, px: 2, height: 32, fontSize: '13px', bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
        >
          Add Expense
        </Button>
      </Stack>

      {/* Search Bar */}
      <Card sx={{ p: 1, mb: 1.5, borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by head, person, description reference, mode..."
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            sx: { borderRadius: 1.5, height: 36, fontSize: '13px' }
          }}
        />
      </Card>

      {/* Data Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#fef2f2" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Sr</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Photo</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Expense Head</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Person</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Assigned Ledger</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Mode</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Daybook</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={12} align="center" sx={{ py: 2 }}><CircularProgress size={20} /></TableCell></TableRow>
            ) : filteredExpenses.length === 0 ? (
              <TableRow><TableCell colSpan={12} align="center" sx={{ py: 3, fontSize: '13px' }}>No records found.</TableCell></TableRow>
            ) : (
              filteredExpenses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((exp, index) => {
                const isPending = exp.approvalStatus === "pending_edit" || exp.approvalStatus === "pending_delete";

                return (
                  <TableRow key={exp.id} hover>
                    <TableCell sx={{ py: 0.5, fontSize: '12.5px' }}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      {exp.imageUrl ? (
                        <Avatar
                          src={exp.imageUrl}
                          variant="rounded"
                          sx={{ cursor: 'pointer', width: 30, height: 30 }}
                          onClick={() => window.open(exp.imageUrl)}
                        />
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 30, height: 30, bgcolor: '#f1f5f9' }}>
                          <Receipt sx={{ color: '#94a3b8', fontSize: '16px' }} />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '12.5px' }}>{exp.date}</TableCell>
                    <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: '12.5px', color: 'text.primary' }}>{exp.expenseHead || "-"}</TableCell>
                    <TableCell sx={{ py: 0.5, fontWeight: 500, fontSize: '12.5px' }}>{exp.personName}</TableCell>
                    <TableCell sx={{ py: 0.5, fontWeight: 500, color: 'error.main', fontSize: '12.5px' }}>
                      {ledgers.find(l => l.id === exp.ledger)?.ledgername || exp.ledger || "-"}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '12px' }}>
                      <Typography variant="body2" sx={{ fontSize: '12.5px', fontWeight: 500 }}>
                        {exp.payMode}
                      </Typography>
                      {exp.bankName && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '10px', mt: -0.2 }}>
                          {exp.bankName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '12.5px', maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {exp.description || "-"}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, color: 'error.main', fontWeight: 700, fontSize: '12.5px' }}>₹{parseFloat(exp.amount).toLocaleString()}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '12.5px', fontWeight: 500, color: exp.dayBook === "No" ? "text.secondary" : "success.main" }}>
                      {exp.dayBook}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>{getStatusChip(exp.approvalStatus)}</TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={isPending ? "Action disabled: Pending Admin Approval" : "Edit Record"}>
                          <span>
                            <IconButton 
                              onClick={() => { 
                                setEditId(exp.id); 
                                setFormData({ ...exp, dayBook: exp.dayBook }); 
                                setOpen(true); 
                              }} 
                              size="small" 
                              color="primary" 
                              disabled={isPending}
                              sx={{ p: 0.5 }}
                            >
                              <Edit sx={{ fontSize: '16px' }} />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title={isPending ? "Action disabled: Pending Admin Approval" : "Request Delete"}>
                          <span>
                            <IconButton 
                              onClick={() => requestDelete(exp)} 
                              size="small" 
                              color="error" 
                              disabled={isPending}
                              sx={{ p: 0.5 }}
                            >
                              <Delete sx={{ fontSize: '16px' }} />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Admin Action Button */}
                        {isAdmin && isPending && (
                          <Tooltip title="Review Approval Request">
                            <IconButton 
                              onClick={() => { setSelectedItemForApproval(exp); setApprovalModalOpen(true); }} 
                              size="small" 
                              color="warning" 
                              sx={{ p: 0.5 }}
                            >
                              <AdminPanelSettings sx={{ fontSize: '18px' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={filteredExpenses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ ".MuiTablePagination-toolbar": { minHeight: 36 }, ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { fontSize: '12px' } }}
        />
      </TableContainer>

      {/* Entry Popup Dialog Form */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, p: 2, fontSize: '16px' }}>{editId ? "Update Expense (Requires Approval)" : "Add New Expense"}</DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            
            <Autocomplete
              freeSolo
              size="small"
              options={uniqueExpenseHeads}
              value={formData.expenseHead || ""}
              onChange={(event, newValue) => setFormData({ ...formData, expenseHead: newValue || "" })}
              onInputChange={(event, newInputValue) => setFormData({ ...formData, expenseHead: newInputValue || "" })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Expense Head"
                  fullWidth
                  InputLabelProps={{ style: { fontSize: 13 } }}
                  inputProps={{ ...params.inputProps, style: { fontSize: 13 } }}
                />
              )}
            />

            <Autocomplete
              freeSolo
              size="small"
              options={uniquePersons}
              value={formData.personName || ""}
              onChange={(event, newValue) => setFormData({ ...formData, personName: newValue || "" })}
              onInputChange={(event, newInputValue) => setFormData({ ...formData, personName: newInputValue || "" })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Recipient Name / Paid To"
                  fullWidth
                  InputLabelProps={{ style: { fontSize: 13 } }}
                  inputProps={{ ...params.inputProps, style: { fontSize: 13 } }}
                />
              )}
            />

            <TextField
              select
              size="small"
              label="Account Book Ledger"
              fullWidth
              value={formData.ledger}
              onChange={e => setFormData({ ...formData, ledger: e.target.value })}
              SelectProps={{ style: { fontSize: 13 } }}
              InputLabelProps={{ style: { fontSize: 13 } }}
            >
              {ledgers.length === 0 ? (
                <MenuItem disabled value="" style={{ fontSize: 13 }}><em>No ledgers configured</em></MenuItem>
              ) : (
                ledgers.map((ledger) => (
                  <MenuItem key={ledger.id} value={ledger.id} style={{ fontSize: 13 }}>
                    {ledger.ledgername}
                  </MenuItem>
                ))
              )}
            </TextField>

            <TextField
              select
              size="small"
              label="Payment Mode"
              fullWidth
              value={formData.payMode || "Cash"}
              onChange={(e) => {
                const mode = e.target.value;
                const clearsBank = !["Online", "Cheque", "Transfer"].includes(mode);
                setFormData({
                  ...formData,
                  payMode: mode,
                  ...(clearsBank && { bankId: "", bankName: "", bankBranch: "", bankCode: "", transactionId: "" }),
                  ...(mode !== "Online" && { transactionId: "" })
                });
              }}
              SelectProps={{ style: { fontSize: 13 } }}
              InputLabelProps={{ style: { fontSize: 13 } }}
            >
              <MenuItem value="Cash" style={{ fontSize: 13 }}>Cash</MenuItem>
              <MenuItem value="Online" style={{ fontSize: 13 }}>Online / UPI</MenuItem>
              <MenuItem value="Cheque" style={{ fontSize: 13 }}>Cheque</MenuItem>
              <MenuItem value="Transfer" style={{ fontSize: 13 }}>Bank Transfer</MenuItem>
            </TextField>

            {["Online", "Cheque", "Transfer"].includes(formData.payMode) && (
              <TextField
                select
                size="small"
                label="Select Bank"
                fullWidth
                value={formData.bankId || ""}
                onChange={(e) => {
                  const targetBank = banks.find(b => b.id === e.target.value);
                  setFormData({
                    ...formData,
                    bankId: targetBank?.id || "",
                    bankName: targetBank?.bankname || "",
                    bankBranch: targetBank?.branch || "",
                    bankCode: targetBank?.code || ""
                  });
                }}
                SelectProps={{ style: { fontSize: 13 } }}
                InputLabelProps={{ style: { fontSize: 13 } }}
              >
                {banks.length === 0 ? (
                  <MenuItem disabled value="" style={{ fontSize: 13 }}><em>No bank accounts setup</em></MenuItem>
                ) : (
                  banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id} style={{ fontSize: 13 }}>
                      {bank.bankname} {bank.code ? `(${bank.code})` : ""}
                    </MenuItem>
                  ))
                )}
              </TextField>
            )}

            {formData.payMode === "Online" && (
              <TextField
                size="small"
                label="Transaction ID / Reference Reference"
                fullWidth
                value={formData.transactionId || ""}
                onChange={e => setFormData({ ...formData, transactionId: e.target.value })}
                inputProps={{ style: { fontSize: 13 } }}
                InputLabelProps={{ style: { fontSize: 13 } }}
              />
            )}

            <TextField
              size="small"
              label="Amount"
              type="number"
              fullWidth
              value={formData.amount}
              onChange={e => setFormData({ ...formData, amount: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><span style={{ fontSize: 13 }}>₹</span></InputAdornment>, style: { fontSize: 13 } }}
              InputLabelProps={{ style: { fontSize: 13 } }}
            />
            <TextField
              size="small"
              label="Date"
              type="date"
              fullWidth
              value={formData.date}
              onChange={e => setFormData({ ...formData, date: e.target.value })}
              InputLabelProps={{ shrink: true, style: { fontSize: 13 } }}
              inputProps={{ style: { fontSize: 13 } }}
            />
            <TextField
              size="small"
              label="Description / Remarks"
              multiline
              rows={2}
              fullWidth
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              inputProps={{ style: { fontSize: 13 } }}
              InputLabelProps={{ style: { fontSize: 13 } }}
            />

            <TextField
              select
              size="small"
              label="Add to Daybook"
              fullWidth
              value={formData.dayBook || "Yes"}
              onChange={e => setFormData({ ...formData, dayBook: e.target.value })}
              SelectProps={{ style: { fontSize: 13 } }}
              InputLabelProps={{ style: { fontSize: 13 } }}
            >
              <MenuItem value="Yes" style={{ fontSize: 13 }}>Yes</MenuItem>
              <MenuItem value="No" style={{ fontSize: 13 }}>No</MenuItem>
            </TextField>

            <Box>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<CloudUpload sx={{ fontSize: '16px' }} />}
                sx={{ borderStyle: 'dashed', py: 0.8, fontSize: '12px', textTransform: 'none' }}
              >
                {file ? file.name : (formData.imageUrl ? "Change Attached Receipt" : "Upload Receipt Proof")}
                <input type="file" hidden onChange={e => setFile(e.target.files[0])} accept="image/*" />
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setOpen(false)} color="inherit" size="small" sx={{ fontSize: '13px' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            size="small"
            color="error"
            sx={{ minWidth: 100, fontSize: '13px', height: 32 }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : (editId ? "Submit for Approval" : "Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Approval Decision Modal */}
      <Dialog open={approvalModalOpen} onClose={() => setApprovalModalOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800, fontSize: '15px' }}>Admin Approval Review</DialogTitle>
        <DialogContent dividers>
          {selectedItemForApproval && (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                <strong>Request Type:</strong> {selectedItemForApproval.approvalStatus === "pending_delete" ? "Deletion Request" : "Edit Request"}
              </Typography>
              <Typography variant="body2">
                <strong>Expense Head:</strong> {selectedItemForApproval.expenseHead || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Recipient:</strong> {selectedItemForApproval.personName}
              </Typography>
              
              {selectedItemForApproval.approvalStatus === "pending_edit" && selectedItemForApproval.pendingData && (
                <Box bgcolor="#f1f5f9" p={1} borderRadius={1}>
                  <Typography variant="caption" fontWeight={700} display="block" color="error">Proposed Changes:</Typography>
                  <Typography variant="caption" display="block">Head: {selectedItemForApproval.pendingData.expenseHead}</Typography>
                  <Typography variant="caption" display="block">Amount: ₹{selectedItemForApproval.pendingData.amount}</Typography>
                  <Typography variant="caption" display="block">Date: {selectedItemForApproval.pendingData.date}</Typography>
                  <Typography variant="caption" display="block">Recipient: {selectedItemForApproval.pendingData.personName}</Typography>
                </Box>
              )}

              <TextField
                size="small"
                label="Approval / Rejection Note"
                fullWidth
                multiline
                rows={2}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Add optional note for audit log..."
                inputProps={{ style: { fontSize: 13 } }}
                InputLabelProps={{ style: { fontSize: 13 } }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button 
            variant="outlined" 
            color="error" 
            size="small" 
            startIcon={<Cancel />}
            onClick={() => handleApprovalAction(selectedItemForApproval, "reject")}
          >
            Reject
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            size="small" 
            startIcon={<CheckCircle />}
            onClick={() => handleApprovalAction(selectedItemForApproval, "approve")}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Expense;